import { useState, Suspense, lazy } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { motion, type Variants } from '@/components/framer-motion'

// Dynamic import for GlassOrb to avoid SSR issues
const GlassOrb = lazy(() => import('@/components/GlassOrb'))

const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
      delayChildren: 0.3,
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

export function HomePage() {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <div className="h-screen overflow-hidden bg-[linear-gradient(146deg,_#d3f9ff_0%,_#a6eeeb_100%)]">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="h-full"
      >
        <div className="h-full w-full flex flex-col items-center justify-center z-20 relative">
          <div className="max-w-md sm:max-w-[500px] w-full space-y-8 text-center px-2">
            <motion.div
              variants={fadeUpVariants}
              className="flex justify-center"
            >
              <img
                src="/assets/images/super-logo.svg"
                alt="Super Logo"
                className="h-10 w-auto"
              />
            </motion.div>

            <motion.div variants={fadeUpVariants} className="space-y-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white hidden">
                AI-powered communications
              </h1>
              <p className="text-gray-800 dark:text-gray-300">
                Your AI communication platform for property management,
                supercharging your customer experience
              </p>
            </motion.div>

            <motion.div variants={fadeUpVariants} className="w-full space-y-4">
              <motion.div
                whileTap={{ scale: 0.98 }}
                className="w-full flex justify-center"
              >
                <motion.div
                  className="w-full max-w-[14rem] rounded-sm"
                  animate={{
                    background: isHovering
                      ? 'linear-gradient(to right, #258568, #2a9173)'
                      : 'linear-gradient(to right,rgb(16, 16, 16),rgb(17, 17, 17))',
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Button
                    asChild
                    className="w-full bg-transparent hover:bg-transparent"
                    size="lg"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <Link to="/sign-up">Get started</Link>
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div variants={fadeUpVariants}>
                <Link
                  to="/sign-in"
                  className="text-sm text-gray-900 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors underline"
                >
                  or Sign in
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <div className="fixed -bottom-60 md:-bottom-70 left-0 w-full flex items-center justify-center mx-auto z-10">
          <motion.div
            variants={fadeUpVariants}
            className="w-96 h-96 md:w-[30rem] md:h-[30rem] mx-auto mt-4"
          >
            <Suspense
              fallback={
                <div className="w-96 h-96 md:w-[30rem] md:h-[30rem] bg-gradient-to-br from-cyan-100/20 to-emerald-100/20 rounded-full animate-pulse" />
              }
            >
              <GlassOrb isHovering={isHovering} />
            </Suspense>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
